"use client";

import { ColumnDef } from "@tanstack/react-table";
import { GameCard } from "modules/cards/model/card";
import { getCardUrl } from "utils/utils";
import SetTooltip from "modules/common/components/Table/SetTooltip";
import { TailwindTooltipCardImage } from "modules/common/components/TailwindTooltipCardImage";
import { CardFinishPrices } from "modules/cards/components/CardFinishPrices";
import Link from "next/link";
import { useSets } from "modules/common/hooks/useSets";
import { useMemo } from "react";
import { useCardModal } from "modules/cards/stores/useCardsStore";
import { useCallback } from "react";
import {
  hasFoilVisual,
  primaryAvailableFinish,
} from "modules/cards/model/finishBehavior";

export function usePrintingsColumns(): ColumnDef<GameCard>[] {
  const { getSetName, getSetByCode } = useSets();
  const {
    modal: { open },
    closeModal,
  } = useCardModal();

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  return useMemo((): ColumnDef<GameCard>[] => {
    return [
      {
        id: "setCardType",
        meta: { width: "3rem", className: "px-0 text-center" },
        header: "",
        cell: ({ row }) => (
          <div className="flex w-full items-center justify-center">
            <SetTooltip
              item={row.original}
              position="right"
              size="3xl"
              iconVariant="chip"
            />
          </div>
        ),
      },
      {
        id: "name",
        header: "Set",
        meta: {
          width: "calc(45% - 3rem)",
          className: "overflow-hidden",
        },
        cell: ({ row }) => {
          const card = row.original;
          const finish = primaryAvailableFinish(card.finishes);
          const isFoil = hasFoilVisual(card.finishes);
          const setCode = card.setCode || "unknown";
          const setName = getSetName(setCode, card.game);
          return (
            <div className="flex h-full min-w-0 items-center overflow-hidden">
              <div className="w-full min-w-0 overflow-hidden">
                <TailwindTooltipCardImage
                  card={card}
                  isFoil={isFoil}
                  placement="right"
                >
                  <Link
                    className="text-primary block w-full overflow-hidden text-ellipsis whitespace-nowrap text-left text-xs hover:underline sm:text-sm"
                    href={getCardUrl(card, finish)}
                    onClick={open ? handleClose : undefined}
                  >
                    {setName || setCode}
                  </Link>
                </TailwindTooltipCardImage>
              </div>
            </div>
          );
        },
      },
      {
        id: "number",
        header: "Num.",
        meta: { width: "15%" },
        cell: ({ row }) => (
          <span className="text-secondary text-xs sm:text-sm">
            {row.original.number}
          </span>
        ),
      },
      {
        id: "price",
        header: "Price",
        meta: { width: "20%", className: "text-center" },
        cell: ({ row }) => (
          <CardFinishPrices
            card={row.original}
            className="text-secondary text-xs sm:text-sm"
            emptyFallback={<span className="text-secondary">-</span>}
          />
        ),
      },
      {
        id: "releaseDate",
        header: "Released",
        meta: { width: "20%", className: "text-right" },
        cell: ({ row }) => {
          const setCode = row.original.setCode || "unknown";
          const releaseDate = getSetByCode(
            setCode,
            row.original.game
          )?.releaseDate;
          if (!releaseDate) return null;
          // Release dates are date-only UTC values — local-zone formatting
          // shows the previous year for Jan 1 releases west of UTC.
          const year = String(new Date(releaseDate).getUTCFullYear());
          return <span className="text-secondary text-sm">{year}</span>;
        },
      },
    ];
  }, [getSetName, getSetByCode, open, handleClose]);
}
